import { AgentRoleSchema, GuardEventSchema } from '@paguz/contracts';
import { z } from 'zod';
import {
  AdminAgentSessionPageSchema,
  AdminAgentTraceSchema,
  type AdminAgentSessionSummary,
  type AdminAgentTrace,
  type LlmCall,
  type ToolTrace,
  type TranscriptMessage,
} from './agent-schemas.ts';

/**
 * Alineación con la API real (`services/api` módulo `agents`): `GET /v1/admin/agent/sessions` y
 * `/trace` devuelven filas del runtime (sesión, turnos, llamadas LLM, herramientas y guardas). Aquí
 * se normalizan a las vistas de la consola; cada esquema acepta también la vista completa (MSW).
 */

const LIVE_STATUSES = new Set(['ACTIVE', 'WAITING_HITL', 'TAKEN_OVER']);
const READ_TOOLS = /^(get_|list_|search_|parse_|resolve_|check_|simulate_|verify_)/;

const RealSessionSchema = z.looseObject({
  id: z.string(),
  agent: z.string(),
  status: z.string(),
  applicationId: z.string().nullable(),
  channel: z.string(),
  startedAt: z.string(),
  lastActivityAt: z.string(),
  role: AgentRoleSchema,
  customerId: z.string().nullable().optional(),
  tokensIn: z.number().optional(),
  tokensOut: z.number().optional(),
  costUsd: z.string().optional(),
  takenOverBy: z.string().nullable().optional(),
  lastMessages: z.array(z.looseObject({ text: z.string().optional() })).optional(),
});
type RealSession = z.infer<typeof RealSessionSchema>;

function toSummary(s: RealSession, extra: Partial<AdminAgentSessionSummary> = {}) {
  const summary = {
    id: s.id,
    agent: s.role,
    status: s.status,
    channel: s.channel,
    customer: s.customerId ? { id: s.customerId, fullName: 'Cliente' } : null,
    applicationId: s.applicationId,
    live: LIVE_STATUSES.has(s.status),
    startedAt: s.startedAt,
    lastActivityAt: s.lastActivityAt,
    turns: 0,
    guardsTriggered: 0,
    tokens: { input: s.tokensIn ?? 0, output: s.tokensOut ?? 0, cacheRead: 0, cacheWrite: 0 },
    costUsd: s.costUsd ?? '0',
    takenOverBy: s.takenOverBy ?? null,
    preview: s.lastMessages?.at(-1)?.text ?? '',
    ...extra,
  };
  return AdminAgentSessionPageSchema.shape.data.element.parse(summary);
}

export const AgentSessionListResponseSchema = z.union([
  AdminAgentSessionPageSchema,
  z
    .object({ data: z.array(RealSessionSchema), nextCursor: z.string().nullable().optional() })
    .transform((page) => ({
      data: page.data.map((s) => toSummary(s)),
      nextCursor: page.nextCursor ?? null,
    })),
]);

const RealTraceSchema = z.object({
  session: RealSessionSchema,
  turns: z.array(
    z.object({
      seq: z.int(),
      role: z.string(),
      displayText: z.string().nullable(),
      metadata: z.record(z.string(), z.unknown()).nullable().optional(),
      createdAt: z.string(),
    }),
  ),
  llmCalls: z.array(
    z.looseObject({
      id: z.string(),
      agentRole: z.string(),
      provider: z.string(),
      model: z.string(),
      effort: z.string(),
      inputTokens: z.number(),
      outputTokens: z.number(),
      cacheReadTokens: z.number(),
      cacheWriteTokens: z.number(),
      latencyMs: z.number(),
      costUsd: z.string(),
      stopReason: z.string().nullable(),
      createdAt: z.string(),
    }),
  ),
  toolInvocations: z.array(
    z.looseObject({
      id: z.string(),
      toolName: z.string(),
      status: z.string(),
      input: z.unknown(),
      output: z.unknown(),
      durationMs: z.number(),
      createdAt: z.string(),
    }),
  ),
  guardEvents: z.array(
    z.looseObject({
      stage: z.string(),
      guard: z.string(),
      outcome: z.string(),
      details: z.record(z.string(), z.unknown()),
      createdAt: z.string(),
    }),
  ),
});
type RealTrace = z.infer<typeof RealTraceSchema>;

const within = (at: string, from: number, to: number) => {
  const t = Date.parse(at);
  return t >= from && t < to;
};

export function realTraceToView(real: RealTrace): AdminAgentTrace {
  const transcript: TranscriptMessage[] = real.turns
    .filter((t) => t.displayText !== null && t.displayText !== '')
    .map((t) => ({
      seq: t.seq,
      role:
        t.role === 'user' || t.role === 'assistant' || t.role === 'human_agent'
          ? t.role
          : 'system_note',
      text: t.displayText ?? '',
      inputMode: null,
      author: t.role === 'human_agent' ? 'Asesor' : null,
      at: t.createdAt,
    }));
  const userTurns = real.turns.filter((t) => t.role === 'user');
  const turns = userTurns.map((turn, i) => {
    const from = Date.parse(turn.createdAt);
    const next = userTurns[i + 1];
    const to = next ? Date.parse(next.createdAt) : Number.POSITIVE_INFINITY;
    const llmCalls: LlmCall[] = real.llmCalls
      .filter((c) => within(c.createdAt, from, to))
      .map((c) => ({
        id: c.id,
        agentRole: AgentRoleSchema.catch('PURCHASE_ASSISTANT').parse(c.agentRole),
        provider: c.provider,
        model: c.model,
        effort: c.effort as LlmCall['effort'],
        inputTokens: c.inputTokens,
        outputTokens: c.outputTokens,
        cacheReadTokens: c.cacheReadTokens,
        cacheWriteTokens: c.cacheWriteTokens,
        latencyMs: c.latencyMs,
        costUsd: c.costUsd,
        stopReason: c.stopReason,
        at: c.createdAt,
      }));
    const tools: ToolTrace[] = real.toolInvocations
      .filter((t) => within(t.createdAt, from, to))
      .map((t) => ({
        id: t.id,
        toolName: t.toolName,
        effect: READ_TOOLS.test(t.toolName) ? 'read' : 'draft',
        status: t.status as ToolTrace['status'],
        input: t.input,
        output: t.output,
        durationMs: Math.round(t.durationMs),
        guardOutcome: null,
        at: t.createdAt,
      }));
    const guards = real.guardEvents
      .filter((g) => within(g.createdAt, from, to))
      .flatMap((g) => {
        const parsed = GuardEventSchema.safeParse({ ...g, at: g.createdAt });
        return parsed.success ? [parsed.data] : [];
      });
    const last = [...llmCalls.map((c) => c.at), ...tools.map((t) => t.at)].sort().at(-1);
    return {
      seq: turn.seq,
      startedAt: turn.createdAt,
      durationMs: last ? Math.max(0, Date.parse(last) - from) : 0,
      llmCalls,
      tools,
      guards,
      skills: [],
      memory: { read: [], written: [] },
      checkpoint: null,
    };
  });
  const sum = (pick: (c: LlmCall) => number) =>
    turns.reduce((acc, t) => acc + t.llmCalls.reduce((a, c) => a + pick(c), 0), 0);
  const session = toSummary(real.session, {
    turns: userTurns.length,
    guardsTriggered: real.guardEvents.filter((g) => g.outcome !== 'PASS').length,
    tokens: {
      input: sum((c) => c.inputTokens),
      output: sum((c) => c.outputTokens),
      cacheRead: sum((c) => c.cacheReadTokens),
      cacheWrite: sum((c) => c.cacheWriteTokens),
    },
    preview: transcript.at(-1)?.text ?? '',
  });
  return { session, traceId: real.session.id, transcript, turns };
}

export const AgentTraceResponseSchema = z.union([
  AdminAgentTraceSchema,
  RealTraceSchema.transform(realTraceToView),
]);
